import { Chip, Stack } from '@mui/material';

import { ComponentLayoutModel } from '../../Models/ComponentLayoutModel';

type LayoutInputContentProps = {
	value: ComponentLayoutModel | undefined;
};

/**
 * Displays a summary of layout properties as chips.
 *
 * IMPORTANT: Keep this component aligned with getComponentLayout.tsx in app-runtime.
 * When adding properties here, ensure they're also added to:
 * - ComponentLayoutModel (this module and app-runtime)
 * - LayoutInputDialog (for editing)
 * - getComponentLayout.tsx (app-runtime for rendering)
 */
export default function LayoutInputContent({ value }: LayoutInputContentProps) {
	if (!value) return null;

	const properties: string[] = [];

	// Size properties
	if (value.Width) properties.push(`W: ${value.Width}`);
	if (value.Height) properties.push(`H: ${value.Height}`);
	if (value.MinWidth) properties.push(`minW: ${value.MinWidth}`);
	if (value.MaxWidth) properties.push(`maxW: ${value.MaxWidth}`);
	if (value.MinHeight) properties.push(`minH: ${value.MinHeight}`);
	if (value.MaxHeight) properties.push(`maxH: ${value.MaxHeight}`);
	if (value.Size) properties.push(`size: ${value.Size}`);

	// Color properties
	if (value.Color) properties.push(`color: ${value.Color}`);
	if (value.BackgroundColor) properties.push(`bgColor: ${value.BackgroundColor}`);
	if (value.FontWeight) properties.push(`weight: ${value.FontWeight}`);

	// Display properties
	if (value.Display) properties.push(`display: ${value.Display}`);
	if (value.FlexDirection) properties.push(`direction: ${value.FlexDirection}`);

	// Flex properties
	if (value.Flex) properties.push(`flex: ${value.Flex}`);
	if (value.FlexGrow) properties.push(`grow: ${value.FlexGrow}`);
	if (value.FlexShrink) properties.push(`shrink: ${value.FlexShrink}`);

	// Margin properties
	if (value.Margin) properties.push(`m: ${value.Margin}`);
	else {
		if (value.MarginTop) properties.push(`mt: ${value.MarginTop}`);
		if (value.MarginBottom) properties.push(`mb: ${value.MarginBottom}`);
		if (value.MarginLeft) properties.push(`ml: ${value.MarginLeft}`);
		if (value.MarginRight) properties.push(`mr: ${value.MarginRight}`);
	}

	// Padding properties
	if (value.Padding) properties.push(`p: ${value.Padding}`);
	else {
		if (value.PaddingTop) properties.push(`pt: ${value.PaddingTop}`);
		if (value.PaddingBottom) properties.push(`pb: ${value.PaddingBottom}`);
		if (value.PaddingLeft) properties.push(`pl: ${value.PaddingLeft}`);
		if (value.PaddingRight) properties.push(`pr: ${value.PaddingRight}`);
	}

	// Border properties
	if (value.Border) properties.push(`border: ${value.Border}`);
	else {
		if (value.BorderTop) properties.push(`bt: ${value.BorderTop}`);
		if (value.BorderBottom) properties.push(`bb: ${value.BorderBottom}`);
		if (value.BorderLeft) properties.push(`bl: ${value.BorderLeft}`);
		if (value.BorderRight) properties.push(`br: ${value.BorderRight}`);
	}
	if (value.BorderWidth) properties.push(`borderW: ${value.BorderWidth}`);
	if (value.BorderStyle) properties.push(`borderStyle: ${value.BorderStyle}`);
	if (value.BorderColor) properties.push(`borderColor: ${value.BorderColor}`);
	if (value.BorderRadius) properties.push(`radius: ${value.BorderRadius}`);

	// Alignment properties
	if (value.AlignItems) properties.push(`align: ${value.AlignItems}`);
	if (value.JustifyContent) properties.push(`justify: ${value.JustifyContent}`);

	if (properties.length === 0) return null;

	return (
		<Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5 }}>
			{properties.map((prop, index) => (
				<Chip key={index} label={prop} size="small" variant="outlined" color="primary" />
			))}
		</Stack>
	);
}
